/*
	boxart as an OpenID Connect client of geekgroup.

	begin() sends the browser to auth.geekgroup.app to sign in (auth-code +
	PKCE); complete() runs on the /auth/callback return, swaps the code for an
	id_token at the token endpoint, and hands it back. The account store then
	exchanges that id_token for a boxart session (api/boxart/auth/geekgroup).

	Google is NOT here — it uses Google Identity Services client-side (see the
	login dialog); this file is only the geekgroup redirect flow.
*/

var HOST  = window.location.hostname
var PROD  = /(?:^|\.)geekgroup\.app$/.test(HOST)
var NGROK = /\.ngrok\.io$|\.ngrok-free\.app$/.test(HOST)

// the issuer's authorization UI, and the token endpoint. In dev the token
// exchange goes through the dev-server proxy (relative -> /oauth proxied to
// ddev): same-origin, so no CORS preflight and no ngrok interstitial in the
// path. AUTHORIZE is a full-page navigation (no CORS), so it points straight
// at the auth app (its own ngrok tunnel on a mobile host).
var AUTHORIZE = (PROD ? 'https://auth.geekgroup.app' : (NGROK ? 'https://auth.eu.ngrok.io' : 'http://localhost:8079')) + '/authorize'
// prod: api.geekgroup.app/oauth/token. dev: the /api/oauth/token alias, which
// rides the SPA's already-active /api dev-proxy (same-origin, no CORS).
var TOKEN     = PROD ? 'https://api.geekgroup.app/oauth/token' : '/api/oauth/token'

var CLIENT_ID = 'boxart'
var SCOPE     = 'openid profile collection'
var STORE_KEY = 'boxart.oidc'

function redirectUri() {

	return window.location.origin + '/auth/callback'

}

// base64url of an ArrayBuffer / Uint8Array
function b64url(buffer) {

	var bytes = new Uint8Array(buffer)
	var str = ''

	for (var i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i])

	return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')

}

function random(bytes) {

	return b64url(window.crypto.getRandomValues(new Uint8Array(bytes || 32)))

}

function challengeFor(verifier) {

	return window.crypto.subtle
		.digest('SHA-256', new TextEncoder().encode(verifier))
		.then(b64url)

}

// the `nonce` claim out of an id_token's payload (no signature check — the
// backend verifies the signature; this only binds the token to our request)
function tokenNonce(jwt) {

	try {

		var part = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')

		return JSON.parse(decodeURIComponent(escape(window.atob(part)))).nonce

	} catch (e) {

		return null

	}

}

export default {

	// kick off "Sign in with geekgroup". returnTo is where to land afterwards.
	// opts.intent ('connect' when the user is already signed in to boxart and is
	// just linking their collection) is a UI hint passed to the authorize screen
	// so it can word the consent as "connect" rather than "sign you in".
	begin: function(returnTo, opts) {

		opts = opts || {}

		var verifier = random(32)
		var state = random(16)
		var nonce = random(16)

		return challengeFor(verifier).then(function(challenge) {

			sessionStorage.setItem(STORE_KEY, JSON.stringify({
				verifier: verifier,
				state: state,
				nonce: nonce,
				returnTo: returnTo || '/',
				// remembered across the redirect so the callback can word itself
				// ('connecting' vs 'signing in')
				intent: opts.intent || ''
			}))

			var q = [
				'client_id=' + encodeURIComponent(CLIENT_ID),
				'redirect_uri=' + encodeURIComponent(redirectUri()),
				'response_type=code',
				'scope=' + encodeURIComponent(SCOPE),
				'state=' + encodeURIComponent(state),
				'nonce=' + encodeURIComponent(nonce),
				'code_challenge=' + encodeURIComponent(challenge),
				'code_challenge_method=S256'
			]

			// a non-standard display hint, read only by our own authorize UI;
			// the token endpoint ignores it
			if (opts.intent) q.push('intent=' + encodeURIComponent(opts.intent))

			q = q.join('&')

			window.location.href = AUTHORIZE + '?' + q

		})

	},

	// the intent stashed by begin() ('connect' | ''), so the callback can word
	// itself. Read this BEFORE complete(), which consumes the stored state.
	intent: function() {

		try { return JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}').intent || '' } catch (e) { return '' }

	},

	// complete the dance on /auth/callback. Resolves
	// { id_token, access_token, returnTo } or { error }.
	complete: function() {

		var params = new URLSearchParams(window.location.search)
		var stored = {}

		try { stored = JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}') } catch (e) { stored = {} }

		sessionStorage.removeItem(STORE_KEY)

		var returnTo = stored.returnTo || '/'

		if (params.get('error')) {

			return Promise.resolve({ error: params.get('error'), returnTo: returnTo })

		}

		var code = params.get('code')
		var state = params.get('state')

		// CSRF: the state we stored must match what came back
		if (!code || !state || state !== stored.state || !stored.verifier) {

			return Promise.resolve({ error: 'invalid_state', returnTo: returnTo })

		}

		return window.fetch(TOKEN, {
			method: 'POST',
			headers: { 'content-type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
			credentials: 'include',
			body: JSON.stringify({
				grant_type: 'authorization_code',
				code: code,
				redirect_uri: redirectUri(),
				client_id: CLIENT_ID,
				code_verifier: stored.verifier
			})
		}).then(function(res) {

			return res.json()

		}).then(function(json) {

			if (!json || !json.id_token) return { error: (json && json.error) || 'token_failed', returnTo: returnTo }

			// replay binding: a token that carries a nonce must carry the one we
			// minted for this sign-in (our IdP always mints one), so a token from
			// a different flow can't be injected. A token with no nonce is left
			// to the state/PKCE checks rather than hard-failing sign-in.
			var tokNonce = tokenNonce(json.id_token)

			if (tokNonce && tokNonce !== stored.nonce) {

				return { error: 'invalid_nonce', returnTo: returnTo }

			}

			return { id_token: json.id_token, access_token: json.access_token, returnTo: returnTo }

		}).catch(function() {

			return { error: 'network', returnTo: returnTo }

		})

	}

}
